import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Music2, Loader2, AlertCircle } from "lucide-react";
import type { TikTokPresets as TikTokPresetsData, TikTokPrivacy } from "@shared/schema";
import { PresetSection, PresetToggle } from "./PresetSection";
import { useTikTokCreatorInfo } from "@/hooks/use-tiktok-creator-info";
import {
  getTikTokValidity,
  TIKTOK_PRIVACY_LABEL_KEY,
  TIKTOK_MUSIC_USAGE_URL,
  TIKTOK_BRANDED_CONTENT_POLICY_URL,
} from "./tiktok-compliance";

const KNOWN_PRIVACY: Exclude<TikTokPrivacy, "">[] = [
  "PUBLIC_TO_EVERYONE",
  "MUTUAL_FOLLOW_FRIENDS",
  "FOLLOWER_OF_CREATOR",
  "SELF_ONLY",
];

export function TikTokPresets({
  value,
  onChange,
  forUserId,
  isPhoto = false,
  videoUrl,
  onBlockedChange,
}: {
  value: TikTokPresetsData;
  onChange: (next: TikTokPresetsData) => void;
  forUserId?: string;
  isPhoto?: boolean;
  videoUrl?: string;
  onBlockedChange?: (blocked: boolean) => void;
}) {
  const { t } = useTranslation();
  const { data: creator, isLoading, isError, error } = useTikTokCreatorInfo(forUserId, true);
  // apiRequest throws Error("<status>: <message>") — strip the status prefix so the
  // real TikTok/token reason (e.g. missing video.publish scope) reaches the user.
  const creatorErrorMessage = error instanceof Error ? error.message.replace(/^\d{3}:\s*/, "") : "";
  const [videoDurationSec, setVideoDurationSec] = useState<number | null>(null);

  const maxDurationSec = creator?.maxVideoPostDurationSec ?? 0;
  const durationExceeded =
    !isPhoto &&
    maxDurationSec > 0 &&
    videoDurationSec !== null &&
    videoDurationSec > maxDurationSec;

  // Guideline 1c: a video longer than the account's max_video_post_duration_sec
  // must block publishing. Report it up so the parent can disable the Post button.
  useEffect(() => {
    onBlockedChange?.(durationExceeded);
  }, [durationExceeded, onBlockedChange]);

  // Reset the measured duration whenever the source changes.
  useEffect(() => {
    setVideoDurationSec(null);
  }, [videoUrl]);

  const privacyOptions = (creator?.privacyLevelOptions ?? []).filter(
    (opt): opt is Exclude<TikTokPrivacy, ""> => (KNOWN_PRIVACY as string[]).includes(opt)
  );
  const commentDisabled = creator?.commentDisabled ?? false;
  const duetDisabled = creator?.duetDisabled ?? false;
  const stitchDisabled = creator?.stitchDisabled ?? false;

  // If the chosen privacy is no longer offered by the account (e.g. options
  // narrowed to SELF_ONLY), clear it so the user must re-pick — never silently
  // send a value the account doesn't allow.
  useEffect(() => {
    if (
      value.privacy &&
      privacyOptions.length > 0 &&
      !privacyOptions.includes(value.privacy as Exclude<TikTokPrivacy, "">)
    ) {
      onChange({ ...value, privacy: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creator?.privacyLevelOptions?.join(",")]);

  const validity = getTikTokValidity(value);
  const accountTooltip = t("contentScheduler.presets.tiktok.disabledByAccount");

  return (
    <PresetSection
      icon={<Music2 size={14} className="text-white/85" />}
      title={t("contentScheduler.presets.tiktok.title")}
    >
      {/* Creator identity (from live creator_info) */}
      <div className="flex items-center gap-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] px-3 py-2">
        {isLoading ? (
          <span className="flex items-center gap-2 text-[11px] text-white/40">
            <Loader2 size={12} className="animate-spin" />
            {t("contentScheduler.presets.tiktok.loadingCreator")}
          </span>
        ) : isError || !creator ? (
          <span className="flex items-start gap-2 text-[11px] text-yellow-400/80">
            <AlertCircle size={12} className="mt-0.5 shrink-0" />
            <span>
              {t("contentScheduler.presets.tiktok.creatorUnavailable")}
              {creatorErrorMessage && (
                <span className="block text-white/40 mt-0.5">{creatorErrorMessage}</span>
              )}
            </span>
          </span>
        ) : (
          <>
            {creator.creatorAvatarUrl ? (
              <img
                src={creator.creatorAvatarUrl}
                alt=""
                className="w-7 h-7 rounded-full object-cover"
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-white/[0.08]" />
            )}
            <div className="min-w-0">
              <div className="text-xs text-white/85 truncate">
                {creator.creatorNickname || creator.creatorUsername}
              </div>
              {creator.creatorUsername && (
                <div className="text-[11px] text-white/40 truncate">@{creator.creatorUsername}</div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Hidden probe to read the source video duration for the max-duration check */}
      {videoUrl && !isPhoto && (
        <video
          src={videoUrl}
          preload="metadata"
          className="hidden"
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (Number.isFinite(d)) setVideoDurationSec(d);
          }}
        />
      )}

      {durationExceeded && (
        <div className="flex items-start gap-1.5 text-[11px] text-red-400">
          <AlertCircle size={12} className="mt-0.5 shrink-0" />
          {t("contentScheduler.presets.tiktok.durationExceeded", {
            max: Math.floor(maxDurationSec),
          })}
        </div>
      )}

      {/* Privacy — populated from creator_info, no default selection */}
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-white/45 font-medium">
          {t("contentScheduler.presets.tiktok.privacy")}
        </span>
        <select
          value={value.privacy}
          onChange={(e) => onChange({ ...value, privacy: e.target.value as TikTokPrivacy })}
          className="mt-1 w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white/90 focus:outline-none focus:border-blue-500/50"
        >
          <option value="" disabled className="bg-[#11151d]">
            {t("contentScheduler.presets.tiktok.privacyPlaceholder")}
          </option>
          {privacyOptions.map((opt) => {
            // Branded content cannot be posted privately.
            const blockedByBranded = opt === "SELF_ONLY" && value.brandContent;
            return (
              <option key={opt} value={opt} disabled={blockedByBranded} className="bg-[#11151d]">
                {t(TIKTOK_PRIVACY_LABEL_KEY[opt])}
              </option>
            );
          })}
        </select>
      </label>

      {/* Interaction toggles — all OFF by default, greyed out when the account disables them */}
      <div className={`grid ${isPhoto ? "grid-cols-1" : "grid-cols-3"} gap-3 pt-1`}>
        <div title={commentDisabled ? accountTooltip : undefined}>
          <PresetToggle
            label={t("contentScheduler.presets.tiktok.allowComments")}
            checked={value.allowComments && !commentDisabled}
            disabled={commentDisabled}
            onChange={(next) => onChange({ ...value, allowComments: next })}
          />
        </div>
        {!isPhoto && (
          <div title={duetDisabled ? accountTooltip : undefined}>
            <PresetToggle
              label={t("contentScheduler.presets.tiktok.allowDuet")}
              checked={value.allowDuet && !duetDisabled}
              disabled={duetDisabled}
              onChange={(next) => onChange({ ...value, allowDuet: next })}
            />
          </div>
        )}
        {!isPhoto && (
          <div title={stitchDisabled ? accountTooltip : undefined}>
            <PresetToggle
              label={t("contentScheduler.presets.tiktok.allowStitch")}
              checked={value.allowStitch && !stitchDisabled}
              disabled={stitchDisabled}
              onChange={(next) => onChange({ ...value, allowStitch: next })}
            />
          </div>
        )}
      </div>

      {/* Commercial Content Disclosure — off by default, reveals Your Brand / Branded Content */}
      <div className="border-t border-white/[0.06] pt-3 space-y-2">
        <PresetToggle
          label={t("contentScheduler.presets.tiktok.disclosure")}
          description={t("contentScheduler.presets.tiktok.disclosureHint")}
          checked={value.commercialDisclosure}
          onChange={(next) =>
            onChange({
              ...value,
              commercialDisclosure: next,
              // turning disclosure off clears both sub-options
              brandOrganic: next ? value.brandOrganic : false,
              brandContent: next ? value.brandContent : false,
            })
          }
        />

        {value.commercialDisclosure && (
          <div className="pl-3 border-l border-white/[0.08] space-y-2">
            <PresetToggle
              label={t("contentScheduler.presets.tiktok.brandOrganic")}
              description={t("contentScheduler.presets.tiktok.brandOrganicHint")}
              checked={value.brandOrganic}
              onChange={(next) => onChange({ ...value, brandOrganic: next })}
            />
            <div
              title={
                value.privacy === "SELF_ONLY"
                  ? t("contentScheduler.presets.tiktok.brandedPrivateConflict")
                  : undefined
              }
            >
              <PresetToggle
                label={t("contentScheduler.presets.tiktok.brandContent")}
                description={t("contentScheduler.presets.tiktok.brandContentHint")}
                checked={value.brandContent}
                disabled={value.privacy === "SELF_ONLY"}
                onChange={(next) => onChange({ ...value, brandContent: next })}
              />
            </div>

            {(value.brandOrganic || value.brandContent) && (
              <div className="text-[11px] text-white/55">
                {value.brandContent
                  ? t("contentScheduler.presets.tiktok.labelPaidPartnership")
                  : t("contentScheduler.presets.tiktok.labelPromotional")}
              </div>
            )}

            {validity === "disclosure_without_subtype" && (
              <div className="flex items-start gap-1.5 text-[11px] text-yellow-400/90">
                <AlertCircle size={12} className="mt-0.5 shrink-0" />
                {t("contentScheduler.presets.tiktok.disclosureRequiresSubtype")}
              </div>
            )}
          </div>
        )}

        <PresetToggle
          label={t("contentScheduler.presets.tiktok.aiGenerated")}
          checked={value.aiGenerated}
          onChange={(next) => onChange({ ...value, aiGenerated: next })}
        />
      </div>

      {validity === "branded_content_private_conflict" && (
        <div className="flex items-start gap-1.5 text-[11px] text-yellow-400/90">
          <AlertCircle size={12} className="mt-0.5 shrink-0" />
          {t("contentScheduler.presets.tiktok.brandedPrivateConflict")}
        </div>
      )}

      {/* Compliance declaration with required legal links */}
      <div className="text-[11px] text-white/40 leading-snug border-t border-white/[0.06] pt-3">
        {value.brandContent ? (
          <Trans
            i18nKey="contentScheduler.presets.tiktok.declarationBranded"
            components={{
              bc: (
                <a
                  href={TIKTOK_BRANDED_CONTENT_POLICY_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-400 hover:underline"
                />
              ),
              music: (
                <a
                  href={TIKTOK_MUSIC_USAGE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-400 hover:underline"
                />
              ),
            }}
          />
        ) : (
          <Trans
            i18nKey="contentScheduler.presets.tiktok.declarationDefault"
            components={{
              music: (
                <a
                  href={TIKTOK_MUSIC_USAGE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-400 hover:underline"
                />
              ),
            }}
          />
        )}
      </div>
    </PresetSection>
  );
}
